import { useCallback, useEffect, useState } from "react";
import { SkeletonList } from "./Skeleton";
import { EnvVarsManager } from "./WorkspaceEnvVars";

/**
 * Instruções em camadas (épico "instruções"):
 *  - GLOBAIS (plataforma): sysadmin cria/edita/remove; demais admins só veem.
 *    Têm prioridade máxima - nenhuma instrução de org as sobrepõe.
 *  - DA ORGANIZAÇÃO: todo admin da org cadastra; lidas sempre antes de responder,
 *    mas abaixo das globais.
 */
interface Instruction {
  id: string;
  scope: "global" | "org";
  orgId: string | null;
  title: string;
  content: string;
  enabled: boolean;
  order: number;
  updatedAt: string;
  updatedBy: string | null;
}

interface Props {
  orgId?: string;
  isSysadmin: boolean;
}

export function InstructionsPanel({ orgId, isSysadmin }: Props) {
  const qs = orgId ? `?orgId=${encodeURIComponent(orgId)}` : "";
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [canEditGlobal, setCanEditGlobal] = useState(false);
  const [globals, setGlobals] = useState<Instruction[]>([]);
  const [orgs, setOrgs] = useState<Instruction[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/admin/instructions${qs}`);
      if (!r.ok) throw new Error("Falha ao carregar as instruções.");
      const j = await r.json();
      setCanEditGlobal(Boolean(j.canEditGlobal));
      setGlobals(j.global ?? []);
      setOrgs(j.org ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Falha ao carregar.");
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => {
    void load();
  }, [load]);

  const create = async (scope: "global" | "org", title: string, content: string) => {
    const r = await fetch(`/api/admin/instructions${qs}`, {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ scope, title, content, enabled: true }),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error ?? "Falha ao criar.");
    await load();
  };

  const update = async (id: string, patch: Partial<Instruction>) => {
    const r = await fetch(`/api/admin/instructions/${id}${qs}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(patch),
    });
    const j = await r.json().catch(() => ({}));
    if (!r.ok) throw new Error(j.error ?? "Falha ao salvar.");
    await load();
  };

  const remove = async (id: string) => {
    const r = await fetch(`/api/admin/instructions/${id}${qs}`, { method: "DELETE" });
    if (!r.ok) {
      const j = await r.json().catch(() => ({}));
      throw new Error(j.error ?? "Falha ao remover.");
    }
    await load();
  };

  if (loading) {
    return (
      <div className="admin-block">
        <SkeletonList rows={5} />
      </div>
    );
  }

  return (
    <>
      {error && <div className="auth-error" style={{ marginBottom: 12 }}>{error}</div>}

      {/* Seção global é EXCLUSIVA do sysadmin - admin da org nem vê */}
      {canEditGlobal && (
        <div className="admin-block">
          <h2>Instruções globais da plataforma</h2>
          <p className="admin-sub">
            Regras que valem para toda a plataforma e têm <strong>prioridade máxima</strong> - lidas antes de
            qualquer resposta. Nenhuma instrução de organização pode sobrepô-las. Só o operador da plataforma
            (você) vê e edita estas.
          </p>
          <InstructionList items={globals} editable onUpdate={update} onRemove={remove} />
          <NewInstruction label="Nova instrução global" onCreate={(t, c) => create("global", t, c)} />
        </div>
      )}

      <div className="admin-block">
        <h2>Instruções desta organização</h2>
        <p className="admin-sub">
          Regras da sua organização, lidas <strong>sempre</strong> antes de responder - mas sempre{" "}
          <strong>abaixo das globais</strong>. Em conflito com uma regra global, a global prevalece.
        </p>
        {!orgId && isSysadmin && (
          <p className="admin-user-meta" style={{ marginBottom: 8 }}>
            Selecione uma organização no topo para ver/editar as instruções dela.
          </p>
        )}
        <InstructionList items={orgs} editable onUpdate={update} onRemove={remove} />
        <NewInstruction label="Nova instrução da organização" onCreate={(t, c) => create("org", t, c)} />
      </div>
      {/* Variáveis de ambiente da ORG: instruções podem referenciá-las por $NOME */}
      <div className="admin-block">
        <EnvVarsManager qs={qs} startOpen />
      </div>

    </>
  );
}

function InstructionList({
  items,
  editable,
  onUpdate,
  onRemove,
}: {
  items: Instruction[];
  editable: boolean;
  onUpdate: (id: string, patch: Partial<Instruction>) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  if (items.length === 0) {
    return <div className="admin-empty small">Nenhuma instrução cadastrada ainda.</div>;
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12 }}>
      {items.map((i) => (
        <InstructionRow key={i.id} item={i} editable={editable} onUpdate={onUpdate} onRemove={onRemove} />
      ))}
    </div>
  );
}

function InstructionRow({
  item,
  editable,
  onUpdate,
  onRemove,
}: {
  item: Instruction;
  editable: boolean;
  onUpdate: (id: string, patch: Partial<Instruction>) => Promise<void>;
  onRemove: (id: string) => Promise<void>;
}) {
  // Minimizada por padrão: com várias instruções, a lista vira um índice
  // escaneável (título + status + prévia); expande só a que for mexer.
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(item.title);
  const [content, setContent] = useState(item.content);
  const [busy, setBusy] = useState(false);
  const [confirmDel, setConfirmDel] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const dirty = editable && (title !== item.title || content !== item.content);

  const run = async (fn: () => Promise<void>) => {
    setBusy(true);
    setErr(null);
    try {
      await fn();
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Falha.");
    } finally {
      setBusy(false);
    }
  };

  const preview = item.content.replace(/\s+/g, " ").trim();

  return (
    <div
      style={{
        border: "1px solid var(--border)",
        borderRadius: 8,
        padding: 10,
        background: item.enabled ? "transparent" : "var(--bg-elevated, rgba(0,0,0,0.03))",
        opacity: item.enabled ? 1 : 0.7,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: open ? 6 : 0 }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          title={open ? "Minimizar" : "Expandir"}
          style={{
            background: "none",
            border: 0,
            cursor: "pointer",
            padding: 2,
            color: "var(--text-dim)",
            display: "grid",
            placeItems: "center",
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden
            style={{ transform: open ? "rotate(90deg)" : undefined, transition: "transform 0.15s ease" }}
          >
            <path d="M6 4l4 4-4 4" />
          </svg>
        </button>
        {open && editable ? (
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Título"
            style={{ flex: 1, padding: "4px 8px", fontSize: 13, fontWeight: 600 }}
          />
        ) : (
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            style={{
              flex: 1,
              display: "flex",
              alignItems: "baseline",
              gap: 8,
              minWidth: 0,
              background: "none",
              border: 0,
              cursor: "pointer",
              font: "inherit",
              color: "inherit",
              textAlign: "left",
              padding: 0,
            }}
          >
            <strong style={{ fontSize: 13, whiteSpace: "nowrap" }}>{item.title}</strong>
            {!open && (
              <span
                className="admin-user-meta"
                style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 }}
              >
                {preview}
              </span>
            )}
          </button>
        )}
        <label className="admin-user-meta" style={{ display: "flex", alignItems: "center", gap: 4, whiteSpace: "nowrap" }}>
          <input
            type="checkbox"
            checked={item.enabled}
            disabled={!editable || busy}
            onChange={(e) => void run(() => onUpdate(item.id, { enabled: e.target.checked }))}
          />
          {item.enabled ? "Ativa" : "Desligada"}
        </label>
      </div>

      {open && (
        <>
          {editable ? (
            <textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={4}
              style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
            />
          ) : (
            <p style={{ whiteSpace: "pre-wrap", margin: 0, fontSize: 13 }}>{item.content}</p>
          )}

          {err && <div className="auth-error" style={{ marginTop: 6 }}>{err}</div>}

          {editable && (
            <div style={{ display: "flex", gap: 8, marginTop: 8, alignItems: "center" }}>
              <button
                className="btn-approve"
                type="button"
                disabled={busy || !dirty}
                onClick={() => void run(() => onUpdate(item.id, { title: title.trim(), content }))}
              >
                {busy ? "Salvando…" : "Salvar"}
              </button>
              {confirmDel ? (
                <>
                  <span className="admin-user-meta">Remover?</span>
                  <button className="btn-reject" type="button" disabled={busy} onClick={() => void run(() => onRemove(item.id))}>
                    Sim, remover
                  </button>
                  <button className="btn-reject" type="button" disabled={busy} onClick={() => setConfirmDel(false)}>
                    Cancelar
                  </button>
                </>
              ) : (
                <button className="btn-reject" type="button" disabled={busy} onClick={() => setConfirmDel(true)}>
                  Remover
                </button>
              )}
              <span className="admin-user-meta" style={{ marginLeft: "auto" }}>
                {item.updatedBy ? `editado por ${item.updatedBy}` : null}
              </span>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function NewInstruction({ label, onCreate }: { label: string; onCreate: (title: string, content: string) => Promise<void> }) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async () => {
    if (!content.trim()) {
      setErr("Informe o conteúdo da instrução.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      await onCreate(title.trim() || "Instrução", content.trim());
      setTitle("");
      setContent("");
      setOpen(false);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Falha ao criar.");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button className="btn-approve" type="button" onClick={() => setOpen(true)}>
        + {label}
      </button>
    );
  }
  return (
    <div style={{ border: "1px dashed var(--border)", borderRadius: 8, padding: 10, marginTop: 4 }}>
      <input
        type="text"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Título (ex.: Tom de voz)"
        style={{ width: "100%", padding: "6px 8px", fontSize: 13, fontWeight: 600, marginBottom: 6 }}
      />
      <textarea
        value={content}
        onChange={(e) => setContent(e.target.value)}
        rows={3}
        placeholder="Conteúdo da instrução (vai direto no system prompt)"
        style={{ width: "100%", padding: "6px 8px", fontSize: 13 }}
      />
      {err && <div className="auth-error" style={{ marginTop: 6 }}>{err}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button className="btn-approve" type="button" disabled={busy} onClick={() => void submit()}>
          {busy ? "Criando…" : "Criar"}
        </button>
        <button className="btn-reject" type="button" disabled={busy} onClick={() => { setOpen(false); setErr(null); }}>
          Cancelar
        </button>
      </div>
    </div>
  );
}
